'use client'

import React, { useEffect } from 'react'
import styles from './styles.module.css'
import ErrorBoundary from '../../../ErrorBoundary'
import useSaleClock from '../../../SaleCampaign/useSaleClock'
import { useRegionOffers } from '../../../RegionOffers'
import { useUrlRegion } from '../../../../utils/useCurrentRegion'
import { renderShopItem } from '../../Shop'
import {
  getCampaignPhase, getSaleCardTag, getSaleBonus, offerItemType, isItemInSale, hasBackendDiscount, PHASE_LIVE, PHASE_TEASER,
} from '../../../../lib/saleCampaign'
import { itemTypeOf, savingOf, formatMoney, formatSaleMoment } from '../saleFormat'
import { pushEcommerceEvent, buildEcommercePayload } from '../../../../utils/dataLayer'

// Every sale price on one grid (anchor #all-deals), grouped like the
// shop (Bundles / Wheel Bases / ...). Cards are the normal shop cards with the
// sale pill, so add-to-cart, colour variants and sold-out states behave exactly
// as on /shop.
//
// An item shows only when (a) the campaign lists it (or it is a limited deal) AND
// (b) the backend currently prices it below regular. (b) keeps the grid honest
// if the backend prices are not loaded yet: nothing is shown as a deal that the
// checkout would charge full price for.
function SaleDealsGrid({ saleCampaign: campaign, saleNowMs, saleClockOffsetMs, titleOverride, subtitleOverride }) {
  // a coarse clock is enough here (sold-out pill swaps), so tick once a minute
  const { nowMs } = useSaleClock(saleNowMs, saleClockOffsetMs, 60000)
  const region = useUrlRegion()
  const { offers, offersLoading } = useRegionOffers()
  const phase = getCampaignPhase(campaign, nowMs)

  const items = (offers || [])
    .filter((i) => i.displayOnShop && !!i.price)
    .filter((i) => !(region && Array.isArray(i.hiddenInRegions) && i.hiddenInRegions.includes(region)))
    .filter((i) => isItemInSale(campaign, nowMs, region, itemTypeOf(i)) && hasBackendDiscount(i))

  useEffect(() => {
    if (!items.length) return
    pushEcommerceEvent('view_item_list', {
      ...buildEcommercePayload(items),
      item_list_id: campaign ? campaign.key : 'sale',
      item_list_name: campaign ? campaign.name : 'Sale',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) return null

  const copy = (campaign && campaign.grid) || {}

  if (phase === PHASE_TEASER) {
    return (
      <section id="all-deals" className={styles.wrap}>
        <div className={styles.inner}>
          <h2 className={styles.title}>{titleOverride || copy.title || `All ${campaign.name} prices`}</h2>
          <p className={styles.lead}>
            {copy.teaserLead || `Prices drop on ${formatSaleMoment(campaign.start)}. Every product in this sale comes with our 30-day money-back guarantee.`}
          </p>
        </div>
      </section>
    )
  }

  const best = items.reduce((acc, i) => {
    const s = savingOf(i)
    return s && (!acc || s.amount.priceCents > acc.amount.priceCents) ? s : acc
  }, null)

  // group by catalog category, ordered like the shop
  const groups = new Map()
  items.forEach((i) => {
    const c = i.category || { type: 'other', displayName: 'More deals', order: 999 }
    if (!groups.has(c.type)) groups.set(c.type, { category: c, items: [] })
    groups.get(c.type).items.push(i)
  })
  const ordered = [...groups.values()].sort((a, b) => (a.category.order || 0) - (b.category.order || 0))

  const withTag = (i) => {
    const tag = getSaleCardTag(campaign, nowMs, region, i)
    const saleBonus = getSaleBonus(campaign, nowMs, region, offerItemType(i))
    if (!tag && !saleBonus) return i
    return { ...i, ...(tag ? { cardTag: tag.label, cardTagVariant: tag.variant } : null), saleBonus }
  }

  return (
    <section id="all-deals" className={styles.wrap}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <h2 className={styles.title}>{titleOverride || copy.title || `All ${campaign.name} prices`}</h2>
          <p className={styles.lead}>
            {subtitleOverride || copy.lead || 'Valid until the sale ends or stock runs out.'}
            {best && <> Save up to <strong>{formatMoney(best.amount)}</strong>.</>}
          </p>
        </header>
        {items.length === 0 && (
          <p className={styles.empty}>Deals are loading. If this stays empty, prices are being updated, so check back in a few minutes.</p>
        )}
        {ordered.map(({ category, items: groupItems }) => (
          <div key={category.type} className={styles.group}>
            <h3 className={styles.groupTitle}>{category.displayName}</h3>
            <div className={styles.grid}>
              {groupItems.map((i, idx) => (
                <ErrorBoundary key={i.id || i.productSetId || itemTypeOf(i) || idx}>
                  {renderShopItem(withTag(i), offersLoading, false, false, -1, offers, region)}
                </ErrorBoundary>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default SaleDealsGrid
